import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import { Add, Schedule, Sports, Visibility, EmojiEvents, History, Lock, LockOpen, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSport } from '../contexts/SportContext';
import { Week, Game } from '../types';
import { weeksAPI, gamesAPI } from '../services/api';
import { getSpreadDisplay } from '../utils/gameUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const Dashboard: React.FC = () => {
  const { selectedSport } = useSport();
  const [currentWeek, setCurrentWeek] = useState<Week | null>(null);
  const [allWeeks, setAllWeeks] = useState<Week[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [createPermission, setCreatePermission] = useState<{
    canCreate: boolean;
    reason: string;
    message: string;
    lastWinner?: { username: string; week_number: number; year: number };
    nextWeek?: { week_number: number; year: number };
  } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, [selectedSport]);

  useEffect(() => {
    // Auto-refresh every 30 seconds to catch lock status changes
    const interval = setInterval(() => {
      loadDashboardData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedSport]);

  const loadDashboardData = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Load all weeks filtered by sport and create permission in parallel
      const [weeks, permission] = await Promise.all([
        weeksAPI.getAll({ sport: selectedSport }),
        weeksAPI.canCreateNext()
      ]);

      setAllWeeks(weeks);
      setCreatePermission(permission);

      // Find current active week for this sport
      const activeWeek = weeks.find(w => w.is_active && w.sport === selectedSport);
      setCurrentWeek(activeWeek || null);

      if (activeWeek) {
        const weekGames = await gamesAPI.getByWeek(activeWeek.id);
        setGames(weekGames);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  const isPicksLocked = () => {
    if (!currentWeek) return false;
    return dayjs.utc().isAfter(dayjs.utc(currentWeek.picks_lock_time));
  };

  const getTimeUntilLock = () => {
    if (!currentWeek) return '';
    
    // Parse lock time as UTC since database stores UTC timestamps
    const lockTime = dayjs.utc(currentWeek.picks_lock_time);
    const now = dayjs.utc();
    
    if (now.isAfter(lockTime)) {
      return 'Picks are locked';
    }
    
    const diff = lockTime.diff(now);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days !== 1 ? 's' : ''} until lock`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m until lock`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} until lock`;
    }
  };

  const canCreateWeek = () => {
    return createPermission?.canCreate || false;
  };

  const formatDateTime = (dateString: string) => {
    // Parse date and convert to Central Time for display
    let date = dayjs(dateString);
    
    // If no timezone info, assume UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-')) {
      date = dayjs.utc(dateString);
    }
    
    return date.tz('America/Chicago').format('ddd, MMM D @ h:mm A z');
  };

  const getPreviousWeeks = () => {
    return allWeeks
      .filter(week => !week.is_active)
      .sort((a, b) => {
        // Sort by year desc, then week desc (most recent first)
        if (a.year !== b.year) return b.year - a.year;
        return b.week_number - a.week_number;
      });
  };

  const getWeekStatus = (week: Week) => {
    const lockTime = new Date(week.picks_lock_time);
    const now = new Date();
    
    if (week.is_active) return 'Active';
    if (now >= lockTime) return 'Completed';
    return 'Upcoming';
  };

  const getWeekStatusColor = (week: Week) => {
    const status = getWeekStatus(week);
    switch (status) {
      case 'Active': return 'success';
      case 'Completed': return 'default';
      case 'Upcoming': return 'warning';
      default: return 'default';
    }
  };

  const handleViewWeekPicks = (weekId: number) => {
    navigate(`/picks?week=${weekId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Current Week" icon={<Schedule />} />
          <Tab label="Previous Weeks" icon={<History />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {!currentWeek ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                No Active Week
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                {createPermission?.message || 'Loading...'}
              </Typography>
              {createPermission?.canCreate ? (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/create-week')}
                >
                  Create New Week
                </Button>
              ) : createPermission?.lastWinner && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>{createPermission.lastWinner.username}</strong> won Week {createPermission.lastWinner.week_number} 
                    and should create the next week.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={currentWeek.sport === 'football' ? '🏈 Football' : '🏀 Basketball'}
                    size="small"
                    color="primary"
                  />
                  <Typography variant="h6">
                    Week {currentWeek.week_number} - {currentWeek.year}
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    label={getTimeUntilLock()}
                    color={isPicksLocked() ? 'error' : 'success'}
                    icon={isPicksLocked() ? <Lock /> : <LockOpen />}
                    variant={isPicksLocked() ? 'filled' : 'outlined'}
                  />
                  <Tooltip title="Refresh lock status">
                    <IconButton
                      size="small"
                      onClick={handleRefresh}
                      disabled={refreshing}
                    >
                      <Refresh sx={{
                        animation: refreshing ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              {isPicksLocked() && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Picks are locked for this week. You can view your selections but cannot make changes.
                </Alert>
              )}
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Created by: {currentWeek.creator_username || 'Unknown'}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Picks lock at: {formatDateTime(currentWeek.picks_lock_time)}
              </Typography>
              
              {/* Debug info - can be removed later */}
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.7 }}>
                Debug: Current time: {dayjs().tz('America/New_York').format('YYYY-MM-DD HH:mm:ss z')} | Lock time: {(() => {
                  let date = dayjs(currentWeek.picks_lock_time);
                  if (!currentWeek.picks_lock_time.includes('Z') && !currentWeek.picks_lock_time.includes('+') && !currentWeek.picks_lock_time.includes('-')) {
                    date = dayjs.utc(currentWeek.picks_lock_time);
                  }
                  return date.tz('America/New_York').format('YYYY-MM-DD HH:mm:ss z');
                })()} | Locked: {isPicksLocked() ? 'YES' : 'NO'}
              </Typography>

              <Box mt={2} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/picks')}
                  startIcon={isPicksLocked() ? <Lock /> : <LockOpen />}
                  color={isPicksLocked() ? 'secondary' : 'primary'}
                >
                  {isPicksLocked() ? 'View My Picks' : 'Make Picks'}
                </Button>
                
                {currentWeek.winner_id === user?.id && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/manage-games')}
                    disabled={isPicksLocked()}
                  >
                    Manage Games
                  </Button>
                )}
                
                {isPicksLocked() && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/results')}
                  >
                    View Results
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {games.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Games This Week ({games.length})
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(280px, 1fr))' }, gap: 2 }}>
                  {games.map((game) => (
                    <Card variant="outlined" key={game.id}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Sports sx={{ mr: 1 }} />
                          <Typography variant="subtitle1">
                            {game.away_team} @ {game.home_team}
                          </Typography>
                        </Box>
                        
                        {game.is_spread_pick && game.spread && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={getSpreadDisplay(game)}
                              size="small" 
                              color="info"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem', 
                                fontWeight: 600,
                                bgcolor: 'info.light',
                                color: 'info.contrastText'
                              }} 
                            />
                          </Box>
                        )}
                        
                        <Typography variant="body2" color="textSecondary">
                          {formatDateTime(game.game_time)}
                        </Typography>
                        {
                          game.is_final && (
                          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                            Final: {game.away_team} {game.away_score} - {game.home_team} {game.home_score}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
        )}

        {currentWeek && (
          <Box mt={3}>
            {createPermission?.canCreate ? (
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => navigate('/create-week')}
              >
                Create Next Week
              </Button>
            ) : createPermission && (
              <Alert severity="info">
                <Typography variant="body2">
                  {createPermission.message}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Previous Weeks
            </Typography>
            
            {getPreviousWeeks().length === 0 ? (
              <Typography variant="body1" color="textSecondary">
                No previous weeks found.
              </Typography>
            ) : (
              <List>
                {getPreviousWeeks().map((week, index) => (
                  <React.Fragment key={week.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={week.sport === 'football' ? '🏈' : '🏀'}
                              size="small"
                              color="primary"
                              sx={{ minWidth: '40px' }}
                            />
                            <Typography variant="subtitle1">
                              Week {week.week_number} - {week.year}
                            </Typography>
                            <Chip
                              label={getWeekStatus(week)}
                              size="small"
                              color={getWeekStatusColor(week) as any}
                            />
                            {week.winner_username && (
                              <Chip
                                label={`Winner: ${week.winner_username}`}
                                size="small"
                                icon={<EmojiEvents />}
                                color="warning"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              Created by: {week.creator_username || 'Unknown'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Lock time: {formatDateTime(week.picks_lock_time)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleViewWeekPicks(week.id)}
                          title="View picks for this week"
                        >
                          <Visibility />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < getPreviousWeeks().length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default Dashboard;
